/**
 * 云平台常量与下拉选项工具
 *
 * 背景：云平台列表原先在 20+ 个组件里各自硬编码，且内容互不一致
 * （部分含 VMware、华为云只出现在账单页），新增平台需散弹式修改且容易漏改。
 * 本文件收敛平台的「数值 ID ↔ 展示名」映射，各调用方只声明自己支持哪些平台。
 *
 * 重要：平台数值 ID 的权威来源是后端。前端下拉在多数场景由以下接口驱动：
 *  - GET  /sme/account/getCloudType
 *  - POST /sme/account/queryCloudPlatforms
 *  - POST /sme/account/queryCloudPlatformsMSP
 *  - POST /sme/account/queryCloudPlatformsByCurrentUser
 * 本文件仅用于后端未提供列表、组件内需要硬编码的场景。ID 必须与后端枚举一致。
 */

// 云平台数值 ID。与后端 accountType / cloudPlatformType / type 字段同一套枚举。
export const CLOUD_PLATFORM_ID = {
  AWS: 0,
  ALIYUN: 1,
  AZURE: 2,
  VMWARE: 3,
  GCP: 4,
  HWC: 5,
  TENCENT: 6
};

// 平台展示名。沿用各处原有的既有写法，避免改动引起 UI 文案变化。
export const CLOUD_PLATFORM_NAME = {
  [CLOUD_PLATFORM_ID.AWS]: 'AWS',
  [CLOUD_PLATFORM_ID.ALIYUN]: 'Aliyun',
  [CLOUD_PLATFORM_ID.AZURE]: 'Azure',
  [CLOUD_PLATFORM_ID.VMWARE]: 'VMware',
  [CLOUD_PLATFORM_ID.GCP]: 'GCP',
  [CLOUD_PLATFORM_ID.HWC]: 'HUAWEI Cloud',
  [CLOUD_PLATFORM_ID.TENCENT]: 'Tencent Cloud'
};

/**
 * 账单模块专用的平台字符串标识。
 * 账单接口用字符串而非数值 ID 区分平台，与 CLOUD_PLATFORM_NAME 不同名，不能混用。
 */
export const CLOUD_PLATFORM_BILLING_TYPE = {
  [CLOUD_PLATFORM_ID.AWS]: 'AWS',
  [CLOUD_PLATFORM_ID.ALIYUN]: 'AlibabaCloud',
  [CLOUD_PLATFORM_ID.HWC]: 'HUAWEICloud',
  [CLOUD_PLATFORM_ID.TENCENT]: 'TencentCloud'
};

/**
 * 账单模块的平台展示名覆盖项。
 * 账单页历史上用厂商全称（Alibaba Cloud），与监控筛选处的简称（Aliyun）不同，
 * 收敛常量时保留该差异以免改变既有 UI 文案。未列出的平台沿用 CLOUD_PLATFORM_NAME。
 */
const CLOUD_PLATFORM_BILLING_NAME_OVERRIDE = {
  [CLOUD_PLATFORM_ID.ALIYUN]: 'Alibaba Cloud'
};

/**
 * 按给定 ID 顺序生成 { id, name } 下拉选项。
 * 各调用方显式声明支持的平台，保留原有的按业务差异化取舍。
 * @param {number[]} ids - 平台数值 ID 数组，决定选项内容与顺序
 * @returns {Array<{id: number, name: string}>}
 */
export function buildPlatformOptions(ids) {
  if (!Array.isArray(ids)) {
    throw new Error('buildPlatformOptions expects an array of platform ids, received: ' + typeof ids);
  }
  return ids.map(id => {
    const name = CLOUD_PLATFORM_NAME[id];
    if (!name) {
      throw new Error('Unknown cloud platform id: ' + id);
    }
    return { id: id, name: name };
  });
}

/**
 * 按给定 ID 顺序生成账单模块下拉选项，额外带 cloudType 字符串字段。
 * @param {number[]} ids - 平台数值 ID 数组
 * @returns {Array<{id: number, name: string, cloudType: string}>}
 */
export function buildBillingPlatformOptions(ids) {
  if (!Array.isArray(ids)) {
    throw new Error('buildBillingPlatformOptions expects an array of platform ids, received: ' + typeof ids);
  }
  return ids.map(id => {
    const name = CLOUD_PLATFORM_BILLING_NAME_OVERRIDE[id] || CLOUD_PLATFORM_NAME[id];
    const cloudType = CLOUD_PLATFORM_BILLING_TYPE[id];
    if (!name) {
      throw new Error('Unknown cloud platform id: ' + id);
    }
    if (!cloudType) {
      throw new Error('Cloud platform id ' + id + ' has no billing cloudType mapping');
    }
    return { id: id, name: name, cloudType: cloudType };
  });
}

/**
 * 使用 AK/SK（含腾讯云 SecretId/SecretKey）作为凭证的平台。
 * GCP 走密钥文件上传、VMware 不走公有云凭证，均不在此列。
 */
export const AK_SK_PLATFORM_IDS = [
  CLOUD_PLATFORM_ID.AWS,
  CLOUD_PLATFORM_ID.ALIYUN,
  CLOUD_PLATFORM_ID.AZURE,
  CLOUD_PLATFORM_ID.HWC,
  CLOUD_PLATFORM_ID.TENCENT
];

/**
 * 判断平台是否使用 AK/SK 凭证。
 * @param {number|string} platformId - 平台 ID，兼容表单传入的字符串
 * @returns {boolean}
 */
export function isAkSkPlatform(platformId) {
  return AK_SK_PLATFORM_IDS.indexOf(Number(platformId)) !== -1;
}

export default {
  CLOUD_PLATFORM_ID,
  CLOUD_PLATFORM_NAME,
  CLOUD_PLATFORM_BILLING_TYPE,
  AK_SK_PLATFORM_IDS,
  buildPlatformOptions,
  buildBillingPlatformOptions,
  isAkSkPlatform
};



// WEBPACK FOOTER //
// ./src/utils/cloudPlatform.js